// MPC-proof borsh payloads for Abstract (EVM) and Starknet → NEAR
// finalization.
//
// Abs/Strk don't have a NEAR light client and aren't on Wormhole; their
// transfers are verified by the NEAR chain-signatures (MPC) network, which
// re-reads the origin tx and signs a `ForeignTxSignPayload`. The omni-bridge
// `fin_transfer` accepts this as `MpcVerifyProofArgs { proof_kind, sign_payload }`
// where `sign_payload = borsh(ForeignTxSignPayload)`.
//
// bridge-sdk-js exposes NO helper for this, so we replicate the borsh layout
// of `near-mpc-contract-interface::types::*` (v0.0.1) here with @zorsh/zorsh.
// The schema is the contract's signed message — it is hashed (sha256) and
// signed by the MPC network, so the bytes MUST be exact. `mpcProof.test.ts`
// validates them against the crate's own insta golden hashes.
//
// borsh facts replicated below:
//   - enum tag = 1 byte (u8), discriminant = declaration order
//   - [u8;N] fixed arrays = N raw bytes (no length prefix) → b.bytes(N)
//   - Vec<T> / Vec<u8> / String = u32-LE length prefix + items
//   - ForeignChainRpcRequest order: Abstract(0) Ethereum(1) Solana(2) Bitcoin(3) Starknet(4)

import { b } from "@zorsh/zorsh";

// ─── primitives ──────────────────────────────────────────────────────────
const Hash256 = b.bytes(32); // Hash256([u8;32]) / EvmTxId / StarknetFelt
const Hash160 = b.bytes(20); // Hash160([u8;20])

// ─── finality enums (unit variants, order = discriminant) ──────────────────
const EvmFinality = b.enum({ Latest: b.unit(), Safe: b.unit(), Finalized: b.unit() });
const StarknetFinality = b.enum({ AcceptedOnL2: b.unit(), AcceptedOnL1: b.unit() });

// ─── extractors (BlockHash=0, Log{log_index:u64}=1) ─────────────────────────
const EvmExtractor = b.enum({ BlockHash: b.unit(), Log: b.struct({ log_index: b.u64() }) });
const StarknetExtractor = b.enum({ BlockHash: b.unit(), Log: b.struct({ log_index: b.u64() }) });

// ─── logs ───────────────────────────────────────────────────────────────────
const EvmLog = b.struct({
  removed: b.bool(),
  log_index: b.u64(),
  transaction_index: b.u64(),
  transaction_hash: Hash256,
  block_hash: Hash256,
  block_number: b.u64(),
  address: Hash160,
  data: b.string(), // a "0x…" hex string (see build_*_mpc_sign_payload in sdk-rs)
  topics: b.vec(Hash256),
});

const StarknetLog = b.struct({
  block_hash: Hash256,
  block_number: b.u64(),
  data: b.vec(Hash256),
  from_address: Hash256,
  keys: b.vec(Hash256),
});

// ─── rpc requests ───────────────────────────────────────────────────────────
const EvmRpcRequest = b.struct({
  tx_id: Hash256, // EvmTxId([u8;32])
  extractors: b.vec(EvmExtractor),
  finality: EvmFinality,
});
const StarknetRpcRequest = b.struct({
  tx_id: Hash256, // StarknetTxId(StarknetFelt)
  finality: StarknetFinality,
  extractors: b.vec(StarknetExtractor),
});

// Unused variants (Solana/Bitcoin) are placeholders that MUST occupy their
// positions so Abstract=0 / Starknet=4 keep the right discriminants. They're
// never serialized (we only emit Abstract/Starknet).
const ForeignChainRpcRequest = b.enum({
  Abstract: EvmRpcRequest,
  Ethereum: EvmRpcRequest,
  Solana: b.unit(),
  Bitcoin: b.unit(),
  Starknet: StarknetRpcRequest,
});

// ─── extracted values ───────────────────────────────────────────────────────
const EvmExtractedValue = b.enum({ BlockHash: Hash256, Log: EvmLog });
const StarknetExtractedValue = b.enum({ BlockHash: Hash256, Log: StarknetLog });
const ExtractedValue = b.enum({
  BitcoinExtractedValue: b.unit(), // index 0 placeholder
  EvmExtractedValue,
  StarknetExtractedValue,
});

// ─── the signed payload ──────────────────────────────────────────────────────
const ForeignTxSignPayloadV1 = b.struct({
  request: ForeignChainRpcRequest,
  values: b.vec(ExtractedValue),
});
export const ForeignTxSignPayloadSchema = b.enum({ V1: ForeignTxSignPayloadV1 });

// MpcVerifyProofArgs { proof_kind: ProofKind (u8 tag), sign_payload: Vec<u8> }.
const MpcVerifyProofArgsSchema = b.struct({
  proof_kind: b.u8(),
  sign_payload: b.bytes(), // variable: u32-LE len + bytes (== Vec<u8>)
});

// `ProofKind::InitTransfer` discriminant (used for fin_transfer).
const PROOF_KIND_INIT_TRANSFER = 0;

// ─── public builders ─────────────────────────────────────────────────────────

export interface EvmLogFields {
  logIndex: bigint;
  transactionIndex: bigint;
  transactionHash: Uint8Array; // 32
  blockHash: Uint8Array; // 32
  blockNumber: bigint;
  address: Uint8Array; // 20
  data: string; // "0x…"
  topics: Uint8Array[]; // each 32
}

/** Build the borsh `ForeignTxSignPayload` for an Abstract (EVM) InitTransfer,
 *  then wrap in `MpcVerifyProofArgs` → the `prover_args` bytes for fin_transfer. */
export function buildEvmMpcProverArgs(txId: Uint8Array, log: EvmLogFields): Uint8Array {
  const signPayload = ForeignTxSignPayloadSchema.serialize({
    V1: {
      request: {
        Abstract: {
          tx_id: txId,
          extractors: [{ Log: { log_index: log.logIndex } }],
          finality: { Latest: {} }, // Abstract default (bridge-cli)
        },
      },
      values: [{ EvmExtractedValue: { Log: evmLogToSchema(log) } }],
    },
  });
  return MpcVerifyProofArgsSchema.serialize({
    proof_kind: PROOF_KIND_INIT_TRANSFER,
    sign_payload: signPayload,
  });
}

export interface StarknetLogFields {
  logIndex: bigint;
  blockHash: Uint8Array; // 32
  blockNumber: bigint;
  data: Uint8Array[]; // each 32
  fromAddress: Uint8Array; // 32
  keys: Uint8Array[]; // each 32
}

/** Build the borsh `ForeignTxSignPayload` for a Starknet InitTransfer →
 *  `MpcVerifyProofArgs` `prover_args` bytes. */
export function buildStarknetMpcProverArgs(txId: Uint8Array, log: StarknetLogFields): Uint8Array {
  const signPayload = ForeignTxSignPayloadSchema.serialize({
    V1: {
      request: {
        Starknet: {
          tx_id: txId,
          finality: { AcceptedOnL2: {} }, // Starknet default (bridge-cli)
          extractors: [{ Log: { log_index: log.logIndex } }],
        },
      },
      values: [{ StarknetExtractedValue: { Log: starknetLogToSchema(log) } }],
    },
  });
  return MpcVerifyProofArgsSchema.serialize({
    proof_kind: PROOF_KIND_INIT_TRANSFER,
    sign_payload: signPayload,
  });
}

function evmLogToSchema(log: EvmLogFields) {
  return {
    removed: false,
    log_index: log.logIndex,
    transaction_index: log.transactionIndex,
    transaction_hash: log.transactionHash,
    block_hash: log.blockHash,
    block_number: log.blockNumber,
    address: log.address,
    data: log.data,
    topics: log.topics,
  };
}

function starknetLogToSchema(log: StarknetLogFields) {
  return {
    block_hash: log.blockHash,
    block_number: log.blockNumber,
    data: log.data,
    from_address: log.fromAddress,
    keys: log.keys,
  };
}
